import { STYTCH_ERROR_MESSAGE_BY_TYPE } from './constants';
import { stytchClient } from './providers/AuthProvider';
import type {
    CheckPasswordResponse,
    LudsCheckPasswordResponse,
} from './types/Auth';

export const getSessionJwt = (): string | null =>
    stytchClient.session.getTokens()?.session_jwt ?? null;

/**
 * Unlike {@link getSessionJwt}, this stays true while the session is alive but
 * the shorter-lived JWT has lapsed.
 */
export const hasActiveStytchSession = (): boolean =>
    stytchClient.session.getSync() != null;

export const getAuthorizationHeader = (): { Authorization: string } => {
    const token = getSessionJwt();
    return {
        Authorization: token != null ? `Bearer ${token}` : '',
    };
};

export const getStytchErrorMessage = (error: Error): string | undefined => {
    function parseStytchErrorCode(errorStr: string): string | undefined {
        const errorMsg = errorStr.split('\n')[0];
        const parts = errorMsg?.split(' ');
        if (parts && parts.length >= 2) {
            return STYTCH_ERROR_MESSAGE_BY_TYPE[parts[1] ?? ''];
        } else {
            return 'There was an error validating your credentials. Please try again.';
        }
    }

    function parseStytchErrorType(errorStr: string): string | undefined {
        try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const errorJson = JSON.parse(errorStr);
            return typeof errorJson === 'object'
                ? STYTCH_ERROR_MESSAGE_BY_TYPE[
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      errorJson.error_type as string
                  ]
                : undefined;
        } catch {
            return undefined;
        }
    }

    return (
        parseStytchErrorCode(error.message) ??
        parseStytchErrorType(error.message)
    );
};

export const checkPasswordStrength = async (
    password: string,
): Promise<CheckPasswordResponse> => {
    const passwordCheck = (await stytchClient.passwords.strengthCheck({
        password,
    })) as unknown as LudsCheckPasswordResponse;

    if (passwordCheck.valid_password) return { validPassword: true };

    const {
        valid_password: validPassword,
        breached_password: breachedPassword,
        feedback: {
            luds_requirements: {
                missing_complexity: missingComplexity,
                missing_characters: missingCharacters,
            },
        },
    } = passwordCheck;
    const message =
        "Password isn't strong enough. It must meet the criteria below.";

    return {
        message,
        validPassword,
        strengthCheck: {
            breachedPassword,
            missingCharacters,
            missingComplexity,
        },
    };
};
